// @flow
import React, { Component } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { NavLink, Link } from 'react-router-dom';

import LeagueAvatar from '../Leagues/LeagueAvatar';
import LeagueName from '../Leagues/LeagueName';

import colors from '../../assets/css/colors';
import { above } from '../../assets/css/media';
import NRLFantasyLogoMenu from '../../assets/images/NRLFantasyLogoMenu.png';

import { isDraftComplete, getDraftState } from '../../helpers/league';

import CloseIcon from '../Icons/Close';
import UserIcon from '../Icons/User';
import ExternalLink from './ExternalLink';

const Container = styled.div`
	position: fixed;
	z-index: 10;
	transform: ${({ open }) => open ? 'translateX(0);' : 'translateX(-110%);'};
	top: 0;
	width: 100%;
	height: 100%;
	background: ${colors.nrlDarkGreen};
	box-shadow: 0 0 23px 0 #000000;
	transition: transform 0.5s ease-in-out;
	will-change: transform;

	display: flex;
	flex-direction: column;

	${above.tablet`
		width: 260px;
	`}
`;

const CloseIconButton = styled.button`
	background: transparent;
	border: 0 none;
	margin: 0;
	padding: 0;


	display: flex;
	align-items: center;
	justify-content: center;

	width: 55px;
	height: 55px;
	flex: 0 0 55px;

	font-size: 16px;
	color: ${colors.nrlGreen};
`;

const LogoutButton = styled.button`
	background: transparent;
	border: 0 none;
	margin: 0;
	padding: 14px 20px;
	cursor: pointer;

	display: flex;
    align-items: center;

	width: 100%;
	height: 50px;
	flex: 0 0 50px;

	background: ${colors.nrlGreen};
	font-size: 14px;
	line-height: 1;
	font-weight: bold;
	color: ${colors.title};
	text-align: left;
`;

const LogoutButtonIconWrapper = styled.div`
	font-size: 16px;
	margin-right: 10px;

	display: flex;
	align-items: center;
`;

const NavWrapper = styled.div`
	overflow-y: auto;
	margin: 10px 20px 20px;

	flex: 0 1 100%;
`;

const InternalMainLink = styled(NavLink)`
	display: block;
	font-size: 12px;
	line-height: 1.2;
	font-weight: bold;
	color: ${colors.nrlGreen};
	text-transform: uppercase;
	text-decoration: none;

	margin: 14px 0;

	&.active {

	}
`;

const NavInternalSubLink = styled(NavLink)`
	display: block;
	font-size: 12px;
	line-height: 1.2;
	font-weight: bold;
	color: #fff;
	text-transform: uppercase;
	text-decoration: none;

	margin: 16px 0;
`;

const NavSubMenu = styled.div`
	margin: 26px 0;
`;

const NavInternalLinks = styled.div`
`;

const NavExternalLinks = styled.div`
	margin-top: 40px;

	@media screen and (min-height: 700px) {
		margin-top: 60px;
	}
`;

const LeagueSummary = styled.div`
	display: flex;
	align-items: center;
	max-width: 100%;

	margin: 16px 0;
`;

const LeagueNameStyled = styled(LeagueName)`
	flex: 1 1 100%;
	width: 100px;


	@media screen and (min-width: 480px) {
		width: 170px;
	}
`;

const LeagueAvatarContainer = styled.div`
	width: 40px;
	height: 40px;
	margin-right: 10px;
	flex: 0 0 40px;
`;

const LogoLink = styled(Link)`
	display: block;
	width: 110px;

	margin: 10px 75px 0;
`;

const Logo = styled.img`
	display: block;
	width: 100%;
`;

const themeLeagueSummary = {
	leagueName: {
		color: '#fff',
		children: {
			commissioner: {
				color: '#fff',
			},
		},
	},
};

type TLeague = {
	id: number,
	name: string,
	commissioner: number,
	firstname?: string,
	lastname?: string,
	first_name?: string,
	last_name?: string,
	avatar_version: number,
	is_club?: boolean,
	size?: number,
	num_teams?: number
};

type Props = {
	open: boolean,
	onClose: Function,
	league?: TLeague,
	user_id: number,
	location: {
		pathname: string
	},
	logout: Function
}

class MenuPopout extends Component<Props> {
	componentDidUpdate(prevProps: Props) {
		const { open, location, onClose } = this.props;

		if (open && prevProps.location.pathname !== location.pathname) {
			onClose();
		}
	}

	componentWillUnmount() {
		const { onClose } = this.props;
		onClose();
	}

	get is_league_draft_complete() {
		const { league } = this.props;
		return league && isDraftComplete(league);
	}

	get is_league_commissioner() {
		const { league, user_id } = this.props;
		return league && league.commissioner === user_id;
	}

	get invite_item() {
		const { league } = this.props;

		if(league && league.size !== league.num_teams) {
			return (
				<NavInternalSubLink to={`/league/${league.id}/invite/`}>
					Invites
				</NavInternalSubLink>
			);
		}
	}

	renderLeagueSummary = () => {
		const { league, user_id } = this.props;

		if (league && this.is_league_draft_complete) {
			const is_commissioner = league.commissioner === user_id;
			const leagueCommissionerProps = is_commissioner ?
				{ firstname: "You are the Commissioner" } :
				{
					firstname: league.firstname || league.first_name,
					lastname: league.lastname || league.last_name,
				};

			return (
				<ThemeProvider theme={themeLeagueSummary}>
					<LeagueSummary>
						<LeagueAvatarContainer>
							<LeagueAvatar league={league}/>
						</LeagueAvatarContainer>
						<LeagueNameStyled
							name={league.name}
							{...leagueCommissionerProps}
						/>
					</LeagueSummary>
				</ThemeProvider>
			);
		}

		return null;
	};

	renderLeagueSubMenu = () => {
		const { league } = this.props;

		if (league) {
			const league_draft_state = getDraftState(league);
			const league_draft_state_team_paths = {
				unscheduled: `/league/${league.id}/team/pre-draft/`,
				future: `/league/${league.id}/team/pre-draft/`,
				active: `/league/${league.id}/team/live-draft/`,
				complete: `/league/${league.id}/team/`,
			};
			const league_team_path = league_draft_state ?
				league_draft_state_team_paths[league_draft_state] :
				league_draft_state_team_paths.complete;

			return (
				<NavSubMenu>
					{this.renderLeagueSummary()}
					{this.is_league_draft_complete && (
						<NavInternalSubLink to={`/league/${league.id}`} exact>
							League Overview
						</NavInternalSubLink>
					)}
					<NavInternalSubLink to={league_team_path}>
						My Team
					</NavInternalSubLink>
					{this.is_league_draft_complete && (
						<NavInternalSubLink to={`/league/${league.id}/matchup/`}>
							Match Ups
						</NavInternalSubLink>
					)}
					{this.is_league_draft_complete && (
						<NavInternalSubLink to={`/league/${league.id}/players/`}>
							Players
						</NavInternalSubLink>
					)}

					{this.is_league_commissioner ? (
						<NavInternalSubLink to={`/league/${league.id}/settings/`}>
							Settings
						</NavInternalSubLink>
					) :
						<NavInternalSubLink to={`/league/${league.id}/about/`}>
						About
						</NavInternalSubLink>
					}

					{ this.invite_item }

				</NavSubMenu>
			);
		}

		return null;
	};

	render() {
		const { open, onClose, logout } = this.props;
		const fantasy_url = process.env.REACT_APP_ABSPATH || false,
			fantasy_help_url = fantasy_url ? fantasy_url + 'help/draft/guidelines' : '';

		return (
			<Container open={open}>
				<CloseIconButton onClick={onClose}><CloseIcon/></CloseIconButton>
				<LogoLink to="/draft/leagues">
					<Logo src={NRLFantasyLogoMenu} alt="NRL Fantasy Logo"/>
				</LogoLink>
				<NavWrapper>
					<NavInternalLinks>
						<InternalMainLink to="/leagues">My Leagues</InternalMainLink>
						{this.renderLeagueSubMenu()}
						{/*Use fantasy Help - NRLDRAFT-202*/}
						{/*<InternalMainLink to="/help">Help</InternalMainLink>*/}
						<ExternalLink href={fantasy_help_url}>
							Help
						</ExternalLink>

					</NavInternalLinks>
					<NavExternalLinks>
						<ExternalLink href={`${process.env.REACT_APP_NRL_FANTASY_LINK || ''}`}>
							Fantasy
						</ExternalLink>
						<ExternalLink href={`${process.env.REACT_APP_NRL_FANTASY_NEWS_LINK || ''}`}>
							Fantasy News
						</ExternalLink>
						<ExternalLink href={`${process.env.REACT_APP_NRL_TIPPING_LINK || ''}`}>
							Tipping
						</ExternalLink>
						<ExternalLink href={`${process.env.REACT_APP_NRL_URL || ''}`}>
							NRL Home
						</ExternalLink>
					</NavExternalLinks>
				</NavWrapper>
				<LogoutButton onClick={logout}>
					<LogoutButtonIconWrapper><UserIcon/></LogoutButtonIconWrapper> Log Out
				</LogoutButton>
			</Container>
		);
	}
}

export default MenuPopout;


// WEBPACK FOOTER //
// ./src/components/MenuPopout/index.js